/**
 * The CoOrdio Cortex application frame — top bar, sidebar, overlays and toasts.
 *
 * This is Cortex's counterpart to CoordioCore's `ShellSurface`, built from the same `@coordio/ui`
 * components so both products render the same chrome. What differs is where the state comes from.
 *
 * WHY it does not use `@coordio/foundation`'s `createShellApp`: that provider models CoordioCore's
 * tenant / organization / product-module world, and Cortex already has its own — MSAL for auth,
 * `contextStore` for tenant and project, `settingsStore` for theme and layout. Adopting Foundation's
 * shell would mean running two competing sources of truth for the same state.
 *
 * Nothing is lost by not adopting it: every shell component here (`AppShell`, `AppSidebar`,
 * `ShellHeaderActions`, `ShellBlockingOverlay`, `ShellTopProgressBar`,
 * `CoordioDropdown`) is prop-driven, and `ShellSurface` itself only uses Foundation's hooks to
 * *compute* those props. The one component that genuinely needs Foundation context is
 * `ShellNotificationPanel`, which reads its own query hooks — so the bell here opens Cortex's own
 * notification surface instead.
 *
 * ── The two E2E markers ────────────────────────────────────────────────────────────────────────
 *
 * `ShellBlockingOverlay` and `ShellTopProgressBar` are not decoration: the Playwright suite waits on
 * them. Both must be *absent from the DOM* when idle, never merely transparent. CoordioCore's
 * `RULES.md` records what happens otherwise — their overlay stayed mounted at `opacity: 0`, which
 * Playwright still counts as visible, so a text or role locator waited ~151 s per call, twice per
 * navigation, on a page that was ready in 5.5 s. Hence the conditional renders below rather than a
 * `visible={false}` prop. See `tests/AgentOps.Web.UI/fixtures/wait-for-app.ts`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';

import {
  AppShell,
  CoordioDropdown,
  ModuleSelector,
  ShellBlockingOverlay,
  ShellHeaderActions,
  ShellToasterHost,
  ShellTopProgressBar,
  type ShellSidebarItem,
} from '@coordio/ui';

import './CortexShellSurface.scss';
import { useAuth } from '../hooks/useAuth';
import { useSessionSync } from '../hooks/useSessionSync';
import { useContextStore } from '../store/contextStore';
import { useSettingsStore } from '../store/settingsStore';

import {
  createCortexSidebarPermissionChecker,
  resolveCortexSidebarItems,
} from './sidebar-config';

import {
  CORE_PRODUCT_OPTIONS,
  CORTEX_PRODUCT_OPTION,
  COORDIO_CORTEX_MODULE,
  hasCoreProductUrl,
  resolveCoreModuleUrl,
} from './product-modules';

import { Folder } from 'lucide-react';
import { DrawerRolesChips } from '../components/DrawerRolesChips';
import type { ReactNode } from 'react';

type CortexShellSurfaceProps = Readonly<{
  /** The app's routes. */
  children: ReactNode;
}>;

/** Brand assets for the sidebar — same CoOrdio house lockup/mark as CoordioCore.
 *
 * WHY the plain CoOrdio lockup (not `coordio-cortex-lockup`): Core switched away from
 * product-specific wordmarks so the shell logo stays house branding; product identity
 * lives elsewhere. Intrinsic lockup is 824×224 — keep width/height in that ratio.
 */
const BRANDING = {
  productName: '',
  lockupSrc: `${import.meta.env.BASE_URL}brand/coordio-lockup.png`,
  markSrc: `${import.meta.env.BASE_URL}brand/coordio-mark.svg`,
  lockupWidth: 132,
  lockupHeight: 36,
  markSize: 28,
  logoAlt: 'CoOrdio',
} as const;

/** Below this width the sidebar becomes a drawer rather than a rail. Matches AppShell's own breakpoint. */
const MOBILE_BREAKPOINT_PX = 768;

export function CortexShellSurface({ children }: CortexShellSurfaceProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  /**
   * Ends this tab when any other CoOrdio app signs out.
   *
   * Placed in the shell rather than in a page so it watches for the whole authenticated session:
   * pages come and go with navigation, and a tab parked on one screen is exactly the tab this is
   * meant to catch.
   */
  useSessionSync({ isAuthenticated, onSignedOutElsewhere: logout });

  const currentTenant = useContextStore((s) => s.currentTenant);
  const availableTenants = useContextStore((s) => s.availableTenants);
  const setTenant = useContextStore((s) => s.setTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const availableProjects = useContextStore((s) => s.availableProjects);
  const setProject = useContextStore((s) => s.setProject);
  const enabledModules = useContextStore((s) => s.enabledModules);
  const isGlobalAccess = useContextStore((s) => s.isGlobalAccess);
  const isContextLoading = useContextStore((s) => s.isLoading);
  const loadTenants = useContextStore((s) => s.loadTenants);
  const loadProjects = useContextStore((s) => s.loadProjects);

  const { settings, updateSettings } = useSettingsStore();

  const [mobileOpen, setMobileOpen] = useState(false);

  // Load tenants on mount (mirrors old TenantSwitcher useEffect)
  useEffect(() => {
    if (availableTenants.length === 0) {
      loadTenants();
    }
  }, [availableTenants.length, loadTenants]);

  // Load projects when tenant changes (mirrors old ProjectSwitcher useEffect)
  useEffect(() => {
    if (currentTenant && availableProjects.length === 0) {
      loadProjects(currentTenant.id);
    }
  }, [currentTenant, availableProjects.length, loadProjects]);

  /**
   * Cortex stores sidebar collapse in `settings.layout` rather than a dedicated flag, so the
   * setting survives a reload with the rest of the layout preferences.
   */
  const sidebarCollapsed = settings.layout === 'collapsed';
  const toggleSidebar = useCallback(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT_PX) {
      setMobileOpen((open) => !open);
      return;
    }
    updateSettings({ layout: sidebarCollapsed ? 'vertical' : 'collapsed' });
  }, [sidebarCollapsed, updateSettings]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX;

  /**
   * Sidebar items are recomputed per navigation: `resolveCortexSidebarItems` suppresses, then
   * permission-filters, then marks the active node, so the tree already carries `isActive` and
   * `AppSidebar` auto-expands the branch containing it.
   */
  const sidebarItems = useMemo<ShellSidebarItem[]>(() => {
    const can = createCortexSidebarPermissionChecker({
      hasTenant: Boolean(currentTenant),
      isGlobalAccess,
      enabledModules,
    });
    return resolveCortexSidebarItems(location.pathname, can);
  }, [currentTenant, isGlobalAccess, enabledModules, location.pathname]);

  const handleSidebarItemNavigate = useCallback(
    (href: string) => {
      const target = href?.trim();
      if (!target) return;
      navigate(target);
      // A drawer that stays open over the page it just navigated to hides the result of the click.
      setMobileOpen(false);
    },
    [navigate],
  );

  /**
   * Top progress bar tracks in-flight React Query fetches.
   *
   * WHY not react-router's navigation state: that is only populated by a data router, and Cortex
   * routes declaratively. Query activity is the honest signal for "the page is still filling in",
   * which is what the bar communicates.
   */
  const activeFetchCount = useIsFetching();

  /**
   * The blocking overlay is reserved for switching workspace, which re-scopes every subsequent
   * request. `contextStore.isLoading` is true exactly while that switch resolves.
   */
  const isSwitchingWorkspace = isContextLoading;

  const tenantOptions = useMemo(
    () => availableTenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
    [availableTenants],
  );

  const handleTenantChange = useCallback(
    (nextId: string | null) => {
      const next = availableTenants.find((tenant) => tenant.id === nextId);
      if (next) setTenant(next);
    },
    [availableTenants, setTenant],
  );

  const projectOptions = useMemo(
    () => availableProjects.map((p) => ({ value: p.id, label: p.name })),
    [availableProjects],
  );

  const handleProjectChange = useCallback(
    (nextId: string | null) => {
      const next = availableProjects.find((p) => p.id === nextId);
      setProject(next ?? null);
    },
    [availableProjects, setProject],
  );

  /**
   * Whether this build knows where CoordioCore lives.
   *
   * Hidden rather than disabled when `VITE_URL_CORE` is unset: a switcher whose every option does
   * nothing is worse than no switcher, because the user cannot tell a misconfigured build from a
   * product they lack access to.
   */
  const showProductSwitcher = useMemo(() => hasCoreProductUrl(), []);

  /**
   * Every CoOrdio product, with Cortex last as the entry the user is currently in.
   *
   * Cortex's own option is `disabled` so it renders as the selector's label without being
   * selectable — there is nowhere to navigate to when you are already here.
   */
  const productOptions = useMemo(
    () => [
      ...CORE_PRODUCT_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
      { value: CORTEX_PRODUCT_OPTION.value, label: CORTEX_PRODUCT_OPTION.label, disabled: true },
    ],
    [],
  );

  /**
   * Opens the chosen CoOrdio product in a new tab, deep-linked to that product's slug.
   *
   * WHY a new tab and not this one: Core is a separate application, and the user chose a second
   * workspace rather than asking to leave the run, dashboard or transcript open here. CoordioCore's
   * own `ShellSurface` opens cross-product links the same way, so the behaviour matches in both
   * directions.
   *
   * The new tab is not prompted to sign in again. It does not inherit this tab's token — MSAL's
   * cache is per-origin and cannot cross — but both products sign in through the same B2C authority
   * host, and B2C's SSO session is a cookie on that host. The new tab therefore round-trips to B2C
   * and comes straight back with a token, which the user sees as a brief redirect rather than a
   * login form. A second login form appearing here means `VITE_B2C_INSTANCE` no longer matches the
   * host CoordioCore uses, not a fault in this handler.
   *
   * `noopener,noreferrer` is required, not cosmetic: without `noopener` the opened document keeps
   * a live `window.opener` handle back into this one.
   */
  const handleProductChange = useCallback((moduleId: string) => {
    const url = resolveCoreModuleUrl(moduleId);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  /**
   * Left slot — the product switcher and the Project selector.
   *
   * WHY the tenant switcher is NOT here any more: the same control already lives in the account
   * drawer as "Workspace" (see `drawerTenantSlot`). Two controls bound to one piece of state is a
   * bug waiting to be reported — they can disagree visually, and a user who changes one has no
   * reason to expect the other moved. The drawer keeps it because that is where CoordioCore puts
   * it, so the two products stay consistent.
   *
   * The product switcher takes its place, mirroring CoordioCore's own top-bar `ModuleSelector`.
   */
  const headerLeft = (
    <div className="cortex-shell__header-switchers">
      {showProductSwitcher ? (
        <div className="cortex-shell__header-module">
          <ModuleSelector
            value={COORDIO_CORTEX_MODULE}
            options={productOptions}
            onValueChange={handleProductChange}
          />
        </div>
      ) : null}
      <div className="cortex-shell__header-switcher">
        <Folder size={18} className="cortex-shell__header-switcher-icon" />
        <CoordioDropdown
          options={projectOptions}
          value={currentProject?.id ?? ''}
          onChange={handleProjectChange}
          placeholder="Select project…"
          searchPlaceholder="Search projects…"
          clearable={false}
          size="sm"
          data-testid="header-project-select"
        />
      </div>
    </div>
  );

  // Center slot is intentionally empty — Tenant and Project are in headerLeft.
  const headerCenter = undefined;

  /**
   * Workspace switcher, inside the user drawer as in CoordioCore.
   *
   * `clearable={false}`: switching workspace re-scopes every request, so it must never be left
   * empty. The dropdown grows a search box on its own once the list passes its threshold.
   */
  const drawerTenantSlot = (
    <div className="cortex-shell__drawer-tenant">
      <span className="cortex-shell__drawer-tenant-label">Tenant Name</span>
      <CoordioDropdown
        options={tenantOptions}
        value={currentTenant?.id ?? ''}
        onChange={handleTenantChange}
        placeholder="Select workspace…"
        searchPlaceholder="Search workspaces…"
        clearable={false}
        size="sm"
        data-testid="drawer-tenant-select"
      />
    </div>
  );

  /**
   * User drawer extras — username + roles, matching CoordioCore's `drawerExtra`.
   *
   * CoordioCore shows `session.friendlyUserName` and `DrawerRolesChips` below the tenant slot.
   * Email is already shown in the user header (via `userMenu.userEmail`), so it is NOT repeated here
   * — same as CoordioCore.
   */
  const drawerExtra = user ? (
    <div className="cortex-shell__drawer-info">
      {user.fullName ? (
        <p className="cortex-shell__drawer-info-row">
          <span className="cortex-shell__drawer-info-label">Username</span>
          <span className="cortex-shell__drawer-info-value">{user.fullName}</span>
        </p>
      ) : null}
      {user.roles && user.roles.length > 0 ? <DrawerRolesChips roles={user.roles} /> : null}
    </div>
  ) : null;

  const headerRight = (
    <ShellHeaderActions
      theme={{
        isDark: settings.mode === 'dark',
        onToggle: () => updateSettings({ mode: settings.mode === 'dark' ? 'light' : 'dark' }),
      }}
      userMenuPresentation="drawer"
      userDrawerTenantSlot={drawerTenantSlot}
      userDrawerExtra={drawerExtra}
      userMenu={{
        userName: user?.fullName,
        userEmail: user?.email,
        onLogout: logout,
      }}
    />
  );

  return (
    <>
      <ShellToasterHost />

      {/*
        Both markers render only while active, so their locators match zero elements when idle.
        See the file header: a permanently mounted, transparent overlay is what cost CoordioCore
        ~151 s per wait.
      */}
      {activeFetchCount > 0 ? <ShellTopProgressBar visible /> : null}
      {isSwitchingWorkspace ? (
        <ShellBlockingOverlay visible message="Switching, please wait…" />
      ) : null}

      <AppShell
        productName={BRANDING.productName}
        logoSrc={sidebarCollapsed && !mobileOpen ? BRANDING.markSrc : BRANDING.lockupSrc}
        logoWidth={sidebarCollapsed && !mobileOpen ? BRANDING.markSize : BRANDING.lockupWidth}
        logoHeight={sidebarCollapsed && !mobileOpen ? BRANDING.markSize : BRANDING.lockupHeight}
        logoAlt={BRANDING.logoAlt}
        sidebarItems={sidebarItems}
        onSidebarItemNavigate={handleSidebarItemNavigate}
        sidebarCollapsed={sidebarCollapsed}
        mobileOpen={mobileOpen}
        onMobileOpenChange={setMobileOpen}
        isMobile={isMobile}
        onToggleSidebar={toggleSidebar}
        headerLeft={headerLeft}
        headerCenter={headerCenter}
        headerRight={headerRight}
      >
        {children}
      </AppShell>
    </>
  );
}
